import React, { useEffect, useState } from 'react';
import { Table, TableProps, Select, Row, Col, Modal, PaginationProps } from 'antd';
import { ExclamationCircleFilled, KeyOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { getRequest, deleteRequest, putRequest, ResponseDataType } from 'api';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { confirm } = Modal;

interface ParentDataType {
    parentId: number;
    name: string;
    dob: string;
    email: string;
    address: string;
    phoneNumber: string;
}

export const ParentList = () => {
    const [parentList, setParentList] = useState<ParentDataType[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [sort, setSort] = useState<string>('desc');
    const [sortBy, setSortBy] = useState<string>('parentId');

    const navigate = useNavigate();

    const sort_by = [
        { label: 'ID', value: 'parentId' },
        { label: 'Họ tên', value: 'name' },
    ];

    const sort_order = [
        { label: 'Giảm dần', value: 'desc' },
        { label: 'Tăng dần', value: 'asc' },
    ];

    const showResetPasswordConfirm = (parent: ParentDataType) => {
        confirm({
            title: 'Đặt lại mật khẩu cho phụ huynh?',
            icon: <KeyOutlined style={{ color: '#fa8c16' }} />,
            content: `Mật khẩu của phụ huynh "${parent.name || ''}" sẽ được đặt lại về chính số điện thoại: ${parent.phoneNumber}.`,
            okText: 'Đặt lại mật khẩu',
            okType: 'primary',
            cancelText: 'Hủy',
            async onOk() {
                const url = `/cms/admin/customer/update-parent-password/${parent.parentId}?newPassword=${encodeURIComponent(parent.phoneNumber)}`;
                const response: ResponseDataType<any> = await putRequest(url);
                if (response?.code === 200) {
                    toast.success(`Đặt lại mật khẩu thành công! Mật khẩu mới là SĐT: ${parent.phoneNumber}`);
                } else {
                    toast.error(response?.msg || 'Đặt lại mật khẩu thất bại');
                }
            },
        });
    };

    const columns: TableProps<ParentDataType>['columns'] = [
        { title: 'ID', dataIndex: 'parentId', key: 'parentId', align: 'center' },
        { title: 'Họ và tên', dataIndex: 'name', key: 'name', align: 'center' },
        { title: 'Ngày sinh', dataIndex: 'dob', key: 'dob', align: 'center' },
        { title: 'Email', dataIndex: 'email', key: 'email', align: 'center' },
        { title: 'Số điện thoại', dataIndex: 'phoneNumber', key: 'phoneNumber', align: 'center' },
        {
            title: 'Hành động',
            key: 'action',
            dataIndex: 'parentId',
            render: (parentId: number, record: ParentDataType) => (
                <>
                    <span
                        style={{ color: 'blue', cursor: 'pointer' }}
                        onClick={() => navigate(`/parent-info/${parentId}`)}
                    >
                        Chi tiết
                    </span>
                    <span
                        style={{ color: '#fa8c16', marginLeft: 10, cursor: 'pointer' }}
                        onClick={() => showResetPasswordConfirm(record)}
                    >
                        Đặt lại mật khẩu
                    </span>
                    <span
                        style={{ color: 'red', marginLeft: 10, cursor: 'pointer' }}
                        onClick={() => showDeleteConfirm(parentId)}
                    >
                        Xóa
                    </span>
                </>
            ),
            align: 'center',
        },
    ];

    const fetchParents = async () => {
        const queryParams = new URLSearchParams({
            page: currentPage.toString(),
            perPage: pageSize.toString(),
            sort: sort,
            sortBy: sortBy,
        });

        const url = `/cms/parent?${queryParams.toString()}`;
        const response: ResponseDataType<{ list: ParentDataType[]; total: number }> = await getRequest(url);

        if (response?.code === 200) {
            setParentList(response.info?.list || []);
            setTotalItems(response.info?.total || 0);
        } else {
            setParentList([]);
            toast.error(response?.msg || 'Lỗi khi tải danh sách phụ huynh');
        }
    };

    const showDeleteConfirm = (parentId: number) => {
        confirm({
            title: 'Bạn có chắc chắn muốn xóa phụ huynh này?',
            icon: <ExclamationCircleFilled />,
            content: 'Hành động này không thể hoàn tác.',
            okText: 'Xác nhận',
            okType: 'danger',
            cancelText: 'Hủy',
            async onOk() {
                const url = `/cms/parent/${parentId}`;
                const response: ResponseDataType<{}> = await deleteRequest(url);
                if (response?.code === 200) {
                    toast.success('Xóa phụ huynh thành công');
                    fetchParents();
                } else {
                    toast.error(response?.msg || 'Xóa thất bại');
                }
            },
        });
    };

    useEffect(() => {
        fetchParents();
    }, [sort, sortBy, currentPage, pageSize]);

    const handlePageChange: PaginationProps['onChange'] = (page, size) => {
        setCurrentPage(page);
        setPageSize(size || 10);
    };

    return (
        <div>
            <Row gutter={8} style={{ marginBottom: 16, alignItems: 'center' }}>
                <Col>
                    <div>Sắp xếp theo:</div>
                    <Select
                        value={sortBy}
                        onChange={setSortBy}
                        style={{ width: 120, marginRight: 8 }}
                    >
                        {sort_by.map((item) => (
                            <Option key={item.value} value={item.value}>
                                {item.label}
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col>
                    <div>Thứ tự:</div>
                    <Select
                        value={sort}
                        onChange={setSort}
                        style={{ width: 120 }}
                    >
                        {sort_order.map((item) => (
                            <Option key={item.value} value={item.value}>
                                {item.label}
                            </Option>
                        ))}
                    </Select>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={parentList}
                rowKey="parentId"
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalItems,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                    onChange: handlePageChange,
                }}
            />
        </div>
    );
};
